import { Injectable } from '@angular/core';
import { combineLatest, fromEvent, merge, Observable, of } from 'rxjs';
import { share, distinctUntilChanged, filter, mergeMap, delay, takeUntil, take, tap } from 'rxjs/operators';
import { ConsoleService } from './console.service';
import { KeyCode } from './keycode.interface';

@Injectable({
  providedIn: 'root'
})
export class KeyboardService {
  public keyEvents$: Observable<KeyboardEvent>;

  constructor(private consoleService: ConsoleService) {
    const keyDown$ = fromEvent<KeyboardEvent>(document, 'keydown');
    const keyUp$ = fromEvent<KeyboardEvent>(document, 'keyup');

    this.keyEvents$ = merge(keyDown$, keyUp$).pipe(
      distinctUntilChanged((a, b) => a.code === b.code && a.type === b.type),
      tap(event => this.consoleService.log('Keyboard event: ' + event.code + ' ' + (event.type == 'keydown' ? 'down' : 'up'))),
      share(),
    );
  }


  all(keys: KeyCode[], hold: number = null): Observable<KeyboardEvent[]> {
    // Create KeyboardEvent Observable for specified KeyCode
    const createKeyPressStream = (key: KeyCode) =>
      this.keyEvents$.pipe(filter((event) => event.code === key.valueOf()));

    // Create Event Stream for every KeyCode in shortcut
    const keyCodeEvents$ = keys.map((key) => createKeyPressStream(key));

    // Emit when specified keys are pressed (keydown).
    // Emit only when all specified keys are pressed at the same time.
    // More on combineLatest below
    const combined$ = combineLatest(keyCodeEvents$);
    const pressed$ = combined$.pipe(
      filter<KeyboardEvent[]>((arr) => arr.every((a) => a.type === 'keydown'))
    );
    const released$ = combined$.pipe(
      filter<KeyboardEvent[]>((arr) => arr.every((a) => a.type === 'keyup'))
    );

    if (hold === null) {
      return pressed$;
    } else {
      return pressed$.pipe(
        mergeMap(event => of(event).pipe(take(1), delay(hold), takeUntil(released$)))
      );
    }
  }


  any(keys: KeyCode[]): Observable<KeyboardEvent> {
    const keyCodeEvents$ = keys.map((key) => this.single(key));
    return merge(...keyCodeEvents$);
  }


  single(code: KeyCode): Observable<KeyboardEvent> {
    return this.keyEvents$.pipe(
      filter(event => event.code === code.valueOf() && event.type === 'keydown')
    );
  }
}
